import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { OwnershipDocumentType } from '@/types';

const DOCUMENT_TYPES: { value: OwnershipDocumentType; labelKey: string }[] = [
  { value: 'id_card', labelKey: 'signature.documentIdCard' },
  { value: 'passport', labelKey: 'signature.documentPassport' },
  { value: 'driving_licence', labelKey: 'signature.documentDrivingLicence' },
  { value: 'company_authorization', labelKey: 'signature.documentCompanyAuthorization' },
];

export interface OwnershipVerificationDialogProps {
  open: boolean;
  submitting: boolean;
  registryOwnerName: string | null;
  onClose: () => void;
  onSubmit: (input: {
    documentType: OwnershipDocumentType;
    matchesRegistry: boolean;
    notes?: string;
  }) => Promise<void>;
}

export function OwnershipVerificationDialog({
  open,
  submitting,
  registryOwnerName,
  onClose,
  onSubmit,
}: OwnershipVerificationDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [documentType, setDocumentType] = useState<OwnershipDocumentType>('id_card');
  const [matchesRegistry, setMatchesRegistry] = useState<'yes' | 'no'>('yes');
  const [notes, setNotes] = useState('');
  const [submitError, setSubmitError] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setDocumentType('id_card');
    setMatchesRegistry('yes');
    setNotes('');
    setSubmitError(false);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(false);
    const trimmed = notes.trim();
    try {
      await onSubmit({
        documentType,
        matchesRegistry: matchesRegistry === 'yes',
        ...(trimmed.length > 0 ? { notes: trimmed } : {}),
      });
      setNotes('');
    } catch {
      setSubmitError(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('signature.ownershipTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('signature.ownershipTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {registryOwnerName
              ? t('signature.ownershipRegistryName', { name: registryOwnerName })
              : t('signature.ownershipRegistryUnknown')}
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid="ownership-binding-note">
            {t('signature.ownershipBindingNote')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="ownership-document-label">
              {t('signature.ownershipDocumentType')}
            </InputLabel>
            <Select
              labelId="ownership-document-label"
              label={t('signature.ownershipDocumentType')}
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value as OwnershipDocumentType)}
            >
              {DOCUMENT_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>{t('signature.ownershipMatches')}</FormLabel>
            <RadioGroup
              row
              value={matchesRegistry}
              onChange={(event) => setMatchesRegistry(event.target.value as 'yes' | 'no')}
            >
              <FormControlLabel
                value="yes"
                control={<Radio />}
                label={t('signature.ownershipMatchesYes')}
              />
              <FormControlLabel
                value="no"
                control={<Radio />}
                label={t('signature.ownershipMatchesNo')}
              />
            </RadioGroup>
          </FormControl>
          <TextField
            label={t('signature.ownershipNotes')}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {t('signature.ownershipSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
